import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const FirebaseAuthContext = createContext();

export const useFirebaseAuth = () => {
  const context = useContext(FirebaseAuthContext);
  if (!context) {
    throw new Error('useFirebaseAuth must be used within a FirebaseAuthProvider');
  }
  return context;
};

export const FirebaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Sign up with email and password
  const signUp = async (email, password, userData = {}) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);

      // Update profile with display name if provided
      if (userData.displayName) {
        await updateProfile(user, {
          displayName: userData.displayName
        });
      }

      // Create user profile in Firestore
      const userProfileData = {
        uid: user.uid,
        email: user.email,
        displayName: userData.displayName || '',
        role: userData.role || null, // Don't set default role
        phone: userData.phone || '',
        createdAt: new Date().toISOString(),
        ...userData
      };

      await setDoc(doc(db, 'users', user.uid), userProfileData);
      setUserProfile(userProfileData);

      return { success: true, user };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      console.log('🔐 Starting Google sign-in process...');
      const provider = new GoogleAuthProvider();
      
      // Add scopes if needed
      provider.addScope('email');
      provider.addScope('profile');
      
      console.log('📱 Attempting Google sign-in popup...');
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      console.log('✅ Google sign-in successful for user:', user.email);

      // Check if user profile exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        console.log('📝 Creating new user profile for Google sign-in...');
        // Get selected role from localStorage
        const selectedRole = localStorage.getItem('selectedRole') || null; // Don't set default role
        
        const userProfileData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          role: selectedRole,
          phone: user.phoneNumber || '',
          createdAt: new Date().toISOString(),
          photoURL: user.photoURL || '',
          provider: 'google'
        };
        
        await setDoc(doc(db, 'users', user.uid), userProfileData);
        console.log('✅ Google sign-in: Created user profile with role:', selectedRole);
      } else {
        console.log('✅ Google sign-in: User profile already exists');
      }

      return { success: true, user };
    } catch (error) {
      console.error('❌ Google sign in error:', error);
      
      // Handle specific Google sign-in errors
      let errorMessage = 'Google sign in failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up was blocked. Please allow pop-ups and try again.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google sign-in is not enabled. Please contact support.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Sign in with Google (fallback method)
  const signInWithGoogleRedirect = async () => {
    try {
      console.log('🔐 Starting Google sign-in with redirect...');
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      await signInWithRedirect(auth, provider);
      return { success: true, message: 'Redirecting to Google...' };
    } catch (error) {
      console.error('❌ Google redirect sign-in error:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle redirect results
  const handleRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        console.log('✅ Google redirect sign-in successful for user:', result.user.email);
        
        // Check if user profile exists, if not create one
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
          console.log('📝 Creating new user profile for Google redirect sign-in...');
          const selectedRole = localStorage.getItem('selectedRole') || null; // Don't set default role
          
          const userProfileData = {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName || '',
            role: selectedRole,
            phone: result.user.phoneNumber || '',
            createdAt: new Date().toISOString(),
            photoURL: result.user.photoURL || '',
            provider: 'google'
          };
          
          await setDoc(doc(db, 'users', result.user.uid), userProfileData);
          console.log('✅ Google redirect sign-in: Created user profile with role:', selectedRole);
        }
        
        return { success: true, user: result.user };
      }
      return { success: false, error: 'No redirect result' };
    } catch (error) {
      console.error('❌ Handle redirect result error:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    try {
      if (user) {
        // Filter out undefined values to prevent Firestore errors
        const cleanUpdates = {};
        Object.keys(updates).forEach(key => {
          if (updates[key] !== undefined) {
            cleanUpdates[key] = updates[key];
          }
        });
        
        await setDoc(doc(db, 'users', user.uid), cleanUpdates, { merge: true });
        setUserProfile(prev => ({ ...prev, ...cleanUpdates }));
        return { success: true };
      }
      return { success: false, error: 'No user logged in' };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  // Load user profile from Firestore
  const loadUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        setUserProfile(profileData);
        return profileData;
      }
      return null;
    } catch (error) {
      console.error('Load profile error:', error);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Handle Google redirect results
    const handleGoogleRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('✅ Google redirect sign-in successful for user:', result.user.email);
          
          // Check if user profile exists, if not create one
          const userDoc = await getDoc(doc(db, 'users', result.user.uid));
          if (!userDoc.exists()) {
            console.log('📝 Creating new user profile for Google redirect sign-in...');
            const selectedRole = localStorage.getItem('selectedRole') || null; // Don't set default role
            
            const userProfileData = {
              uid: result.user.uid,
              email: result.user.email,
              displayName: result.user.displayName || '',
              role: selectedRole,
              phone: result.user.phoneNumber || '',
              createdAt: new Date().toISOString(),
              photoURL: result.user.photoURL || '',
              provider: 'google'
            };
            
            await setDoc(doc(db, 'users', result.user.uid), userProfileData);
            console.log('✅ Google redirect sign-in: Created user profile with role:', selectedRole);
          }
          
          // Set the sign-in successful flag for navigation
          localStorage.setItem('signInSuccessful', 'true');
        }
      } catch (error) {
        console.error('❌ Handle Google redirect error:', error);
      }
    };

    handleGoogleRedirect();

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGoogleRedirect,
    handleRedirectResult,
    logout,
    resetPassword,
    updateUserProfile,
    loadUserProfile
  };

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
}; 